import React, { useState } from "react";
import { Button, Form, message, Input } from 'antd';
import { Link, useNavigate } from 'react-router-dom'
import { LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import { sendEmailCode, emailResetPassword } from "../../API";
import { useTranslation } from 'react-i18next';

import '../login/login.scss'

const Forgot = () => {

    const navigate = useNavigate()
    const { t } = useTranslation();

    const [form] = Form.useForm();

    // 发送验证码 60s 倒计时
    const [countdown, setCountdown] = useState(0);
    const [sending, setSending] = useState(false);

    // 邮箱格式校验
    const validateEmail = (_, value) => {
        if (!value) {
            return Promise.reject(new Error(t('register.emailRequired')));
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return Promise.reject(new Error(t('register.emailInvalid')));
        }
        return Promise.resolve();
    };

    // 密码校验：8-20位且必须同时包含字母和数字
    const validatePassword = (_, value) => {
        if (!value) {
            return Promise.reject(new Error(t('forgot.passwordRequired')));
        }
        if (value.length < 8 || value.length > 20) {
            return Promise.reject(new Error(t('forgot.passwordLength')));
        }
        if (!/[a-zA-Z]/.test(value)) {
            return Promise.reject(new Error(t('forgot.passwordMustContainLetter')));
        }
        if (!/\d/.test(value)) {
            return Promise.reject(new Error(t('forgot.passwordMustContainDigit')));
        }
        return Promise.resolve();
    };

    // 确认密码校验
    const validateConfirmPassword = ({ getFieldValue }) => ({
        validator(_, value) {
            if (!value) {
                return Promise.reject(new Error(t('forgot.confirmPasswordRequired')));
            }
            if (getFieldValue('password') !== value) {
                return Promise.reject(new Error(t('forgot.confirmPasswordNotMatch')));
            }
            return Promise.resolve();
        },
    });

    // 发送邮箱验证码
    const handleSendCode = () => {
        form.validateFields(['email']).then(values => {
            setSending(true);
            sendEmailCode({ email: values.email, type: "RESET_PWD" }).then(res => {
                if (res?.data?.code === 200) {
                    message.success(t('forgot.codeSentSuccess'));
                    setCountdown(60);
                    const timer = setInterval(() => {
                        setCountdown(prev => {
                            if (prev <= 1) {
                                clearInterval(timer);
                                return 0;
                            }
                            return prev - 1;
                        });
                    }, 1000);
                } else {
                    message.error(res?.data?.msg || t('forgot.codeSendFailed'));
                }
            }).catch(err => {
                console.log(err);
                message.error(t('forgot.codeSendFailed'));
            }).finally(() => {
                setSending(false);
            });
        }).catch(() => {
            // 邮箱校验不通过
        });
    };

    // 重置密码提交
    const onFinish = (values) => {
        emailResetPassword({
            email: values.email,
            verifyCode: values.verifyCode,
            newPassword: values.password,
            confirmPassword: values.confirmPassword,
            type: "RESET_PWD",
        }).then(res => {
            if (res?.data?.code === 200) {
                message.success(t('forgot.resetSuccess'));
                localStorage.removeItem("token");
                navigate('/login');
            } else {
                message.error(res?.data?.msg || t('forgot.resetFailed'));
            }
        }).catch(err => {
            console.log(err);
            message.error(t('forgot.resetFailed'));
        });
    };

    const initialValues = {
        email: '',
        verifyCode: '',
        password: '',
        confirmPassword: '',
    };

    return (
        <div className="login">
            <div className="left">
                <div className="car">
                    <img src={process.env.PUBLIC_URL + '/logo/car.png'} alt="" />
                </div>
            </div>
            <div className="right">
                <div className="title">
                    <div className="logo">
                        <img src={process.env.PUBLIC_URL + '/logo/logo.jpg'} alt='logo' width="54px" height='41px' />
                        {t('login.platform')}
                    </div>
                </div>
                <div className="type">{t('forgot.title')}</div>
                <Form
                    name="forgot_form"
                    className="login-form register-form"
                    form={form}
                    initialValues={initialValues}
                    autoComplete="off"
                    onFinish={onFinish}
                >
                    <Form.Item
                        name="email"
                        rules={[{ validator: validateEmail }]}
                    >
                        <Input
                            prefix={<React.Fragment><MailOutlined className="site-form-item-icon" />&nbsp;{t('register.email')}</React.Fragment>}
                            placeholder={t('register.emailPlaceholder')}
                        />
                    </Form.Item>

                    {/* 验证码 + 发送按钮 */}
                    <Form.Item
                        name="verifyCode"
                        rules={[
                            { required: true, message: t('forgot.codeRequired') },
                            { pattern: /^\d{6}$/, message: t('forgot.codeInvalid') }
                        ]}
                    >
                        <div className="code-row">
                            <Input
                                className="code-row-input"
                                prefix={<React.Fragment><SafetyOutlined className="site-form-item-icon" />&nbsp;{t('forgot.verifyCode')}</React.Fragment>}
                                placeholder={t('forgot.codePlaceholder')}
                            />
                            <Button
                                className="code-row-btn"
                                onClick={handleSendCode}
                                disabled={countdown > 0 || sending}
                            >
                                {countdown > 0 ? `${countdown}s` : t('forgot.sendCode')}
                            </Button>
                        </div>
                    </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[{ validator: validatePassword }]}
                    >
                        <Input.Password
                            prefix={<React.Fragment><LockOutlined className="site-form-item-icon" />&nbsp;{t('forgot.newPassword')}</React.Fragment>}
                            placeholder={t('forgot.passwordPlaceholder')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        dependencies={['password']}
                        rules={[validateConfirmPassword]}
                    >
                        <Input.Password
                            prefix={<React.Fragment><LockOutlined className="site-form-item-icon" />&nbsp;{t('forgot.confirmPassword')}</React.Fragment>}
                            placeholder={t('forgot.confirmPasswordPlaceholder')}
                        />
                    </Form.Item>

                    <div className="login-links">
                        <Link to="/register">{t('forgot.goRegister')}</Link>
                        <Link to="/login">{t('forgot.goLogin')}</Link>
                    </div>

                    <Form.Item style={{ margin: "20px 0 15px 0" }}>
                        <Button style={{ width: "100%" }} type="primary" htmlType="submit" className="login-form-button subButton">
                            {t('forgot.submitBtn')}
                        </Button >
                    </Form.Item>
                </Form>
            </div>
            <footer>©2025昌谷（宁波）  备案号：<a target="_blank" href="https://beian.miit.gov.cn/#/Integrated/index">浙ICP备2023036666号-1</a></footer>
        </div>
    )
}

export { Forgot }
